/**
 * 🛡️ АТОМАРНОЕ СОХРАНЕНИЕ БРОНИРОВАНИЙ
 * Единственный источник истины для всех изменений бронирований
 * 
 * @module useAtomicBookingSave
 * @description Гарантирует:
 * - Все расчёты цен на сервере (не на клиенте)
 * - Обязательная причина изменения для аудита
 * - Транзакционная атомарность
 * - Защита от race conditions
 * - Полное логирование изменений
 */

import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { useRealtime } from '@/contexts/RealtimeContext';

export interface BookingChanges {
  // Даты и время
  check_in_date?: string;
  check_out_date?: string;
  check_in_time?: string;
  check_out_time?: string;
  
  // Номер и статус
  room_id?: string;
  room_type?: string;
  status?: string;
  source?: string;
  
  // Гости (все поля для AK-47 синхронизации)
  number_of_guests?: number;
  adult_count?: number;
  children_count?: number;
  infants_count?: number;
  cribs_count?: number;
  extra_beds?: number;
  
  // Комментарии и пожелания
  special_requests?: string;
  dietary_restrictions?: string;
  
  // Финансы
  rate_plan_id?: string | null;
  price_locked?: boolean;
  manual_total_amount?: number;
  base_price?: number;
  total_amount?: number;
  discount_percentage?: number;
  discount_amount?: number;
  
  // VIP и дополнительные опции
  vip_status?: boolean;
  transfer_needed?: boolean;
  no_relocation?: boolean;

  // 🆕 AK-47 v8: Покомпонентный snapshot
  nightly_snapshot?: any | null;
  snapshot_mode?: 'auto' | 'locked' | 'manual' | null;
  expected_snapshot_version?: number;
}

export interface AtomicSaveResult {
  success: boolean;
  booking_id: string;
  old_total: number;
  new_total: number;
  price_locked: boolean;
  changes_count: number;
  change_reason: string;
  calculation_source: 'manual_override' | 'server_rpc_calculation';
}

export interface AtomicSaveError {
  code: 'CHANGE_REASON_REQUIRED' | 'BOOKING_NOT_FOUND' | 'INVALID_DATES' | 'UNKNOWN';
  message: string;
}

/**
 * 🎯 Хук для атомарного сохранения бронирований
 * Заменяет все прямые вызовы update() на booking_core
 */
export const useAtomicBookingSave = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { broadcastBookingChange } = useRealtime();

  /**
   * 💾 Атомарное сохранение изменений бронирования
   * 
   * @param bookingId - ID бронирования
   * @param hotelId - ID отеля
   * @param changes - Объект с изменёнными полями
   * @param changeReason - ОБЯЗАТЕЛЬНАЯ причина изменения для аудита
   * @returns Результат операции или null при ошибке
   */
  const saveBookingAtomic = useCallback(async (
    bookingId: string,
    hotelId: string,
    changes: BookingChanges,
    changeReason: string
  ): Promise<AtomicSaveResult | null> => {
    // 🛡️ Клиентская валидация причины
    if (!changeReason || changeReason.trim() === '') {
      toast({
        title: "Требуется причина изменения",
        description: "Укажите причину изменения для аудита",
        variant: "destructive",
      });
      return null;
    }

    try {
      console.log('🔄 Атомарное сохранение:', { bookingId, changes, changeReason });

      const { data, error } = await supabase.rpc('save_booking_atomic', {
        p_booking_id: bookingId,
        p_hotel_id: hotelId,
        p_changes: changes as any,
        p_change_reason: changeReason,
        // В БД есть перегрузки save_booking_atomic; явный аргумент снимает неоднозначность (PGRST203)
        p_changed_by: null,
      });

      if (error) {
        // Парсим код ошибки
        const errorCode = error.message.includes('CHANGE_REASON_REQUIRED') 
          ? 'CHANGE_REASON_REQUIRED'
          : error.message.includes('BOOKING_NOT_FOUND')
          ? 'BOOKING_NOT_FOUND'
          : error.message.includes('INVALID_DATES')
          ? 'INVALID_DATES'
          : 'UNKNOWN';

        console.error('❌ Ошибка атомарного сохранения:', error);

        toast({
          title: "Ошибка сохранения",
          description: getErrorMessage(errorCode, error.message),
          variant: "destructive",
        });

        return null;
      }

      const result = data as unknown as AtomicSaveResult;

      // ✅ Инвалидируем кэши для обновления UI
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['optimized-bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['bookings'] }),
        queryClient.invalidateQueries({ queryKey: ['booking', bookingId] }),
      ]);

      // 📢 AK-47: Broadcast для синхронизации ДРУГИХ компьютеров
      broadcastBookingChange('UPDATE', bookingId);

      // 🎉 Уведомляем об успехе
      if (result.old_total !== result.new_total) {
        toast({
          title: "Бронирование обновлено",
          description: `Сумма: ${result.old_total.toLocaleString()} → ${result.new_total.toLocaleString()} ₽`,
        });
      } else {
        toast({
          title: "Бронирование обновлено",
          description: `Изменено ${result.changes_count} полей`,
        });
      }

      console.log('✅ Атомарное сохранение успешно:', result);

      return result;

    } catch (error: any) {
      console.error('❌ Непредвиденная ошибка:', error);
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось сохранить изменения",
        variant: "destructive",
      });
      return null;
    }
  }, [toast, queryClient, broadcastBookingChange]);

  /**
   * 🔄 Быстрое обновление статуса (с автоматической причиной)
   */
  const updateBookingStatus = useCallback(async (
    bookingId: string,
    hotelId: string,
    newStatus: string,
    reason?: string
  ): Promise<boolean> => {
    const statusReasons: Record<string, string> = {
      'confirmed': 'Бронирование подтверждено',
      'checked_in': 'Гость заселён',
      'checked_out': 'Гость выселен',
      'cancelled': 'Бронирование отменено',
      'no_show': 'Гость не прибыл',
    };

    const result = await saveBookingAtomic(
      bookingId,
      hotelId,
      { status: newStatus },
      reason || statusReasons[newStatus] || `Статус изменён на ${newStatus}`
    );

    return result !== null;
  }, [saveBookingAtomic]);

  /**
   * 📦 Перемещение бронирования (drag-and-drop)
   */
  const moveBooking = useCallback(async (
    bookingId: string,
    hotelId: string,
    newRoomId: string,
    newCheckIn: string,
    newCheckOut: string,
    reason?: string
  ): Promise<boolean> => {
    const result = await saveBookingAtomic(
      bookingId,
      hotelId,
      {
        room_id: newRoomId,
        check_in_date: newCheckIn,
        check_out_date: newCheckOut,
      },
      reason || 'Перемещение через шахматку'
    );

    return result !== null;
  }, [saveBookingAtomic]);

  /**
   * 💰 Обновление цены (с обязательной фиксацией)
   */
  const updateBookingPrice = useCallback(async (
    bookingId: string,
    hotelId: string,
    newAmount: number,
    reason: string
  ): Promise<boolean> => {
    const result = await saveBookingAtomic(
      bookingId,
      hotelId,
      {
        price_locked: true,
        manual_total_amount: newAmount,
      },
      reason
    );

    return result !== null;
  }, [saveBookingAtomic]);

  return {
    saveBookingAtomic,
    updateBookingStatus,
    moveBooking,
    updateBookingPrice,
  };
};

/**
 * Получает читаемое сообщение об ошибке
 */
function getErrorMessage(code: string, originalMessage: string): string {
  switch (code) {
    case 'CHANGE_REASON_REQUIRED':
      return 'Причина изменения обязательна для аудита';
    case 'BOOKING_NOT_FOUND':
      return 'Бронирование не найдено или было удалено';
    case 'INVALID_DATES':
      return 'Дата выезда должна быть позже даты заезда';
    default:
      return originalMessage || 'Неизвестная ошибка';
  }
}

export default useAtomicBookingSave;
